import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import type { NsMap, RecordRole } from "@/api/netsuiteMap";
import { CommentButton, MONO, TONES, targetId } from "./shared";

// Who creates each NetSuite record. One row per record, every system that
// touches it listed in a wrapping row, so the table never scrolls sideways.
const ROLE_LABEL: Record<RecordRole, string> = { creates: "Creates", updates: "Updates", by_hand: "By hand" };
const ROLE_ORDER: Record<RecordRole, number> = { creates: 0, updates: 1, by_hand: 2 };

export function RoleMark({ role }: { role: RecordRole }) {
  const sx = {
    creates: { bgcolor: "secondary.main", color: "#fff" },
    updates: { border: 1, borderColor: "secondary.main", color: TONES.accent.fg },
    by_hand: { bgcolor: TONES.manual.bg, color: TONES.manual.fg },
  }[role];
  return (
    <Box component="span" sx={{ fontFamily: MONO, fontSize: 10.5, px: 0.75, py: "1px", borderRadius: "3px", whiteSpace: "nowrap", ...sx }}>
      {ROLE_LABEL[role]}
    </Box>
  );
}

export function WhoCreatesTable({ map, counts, onComment }: {
  map: NsMap;
  counts: Record<string, number>;
  onComment: (key: string) => void;
}) {
  const label = Object.fromEntries(map.record_sources.map((s) => [s.id, s.label]));
  const order = Object.fromEntries(map.record_sources.map((s, i) => [s.id, i]));

  return (
    <Stack spacing={1}>
      <Paper variant="outlined">
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <colgroup>
            <Box component="col" sx={{ width: { xs: 130, sm: 220 } }} />
            <col />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>NetSuite record</TableCell>
              <TableCell>Created or updated by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map.records.map((f) => [
              <TableRow key={f.family}>
                <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontFamily: MONO, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "text.secondary", py: 0.75 }}>
                  {f.family}
                </TableCell>
              </TableRow>,
              ...f.rows.map((r) => {
                const by = [...r.by].sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || order[a.source] - order[b.source]);
                return (
                  <TableRow key={r.id} hover sx={{ verticalAlign: "top" }}>
                    <TableCell>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                          <Typography sx={{ fontFamily: MONO, fontSize: 11, color: "text.secondary", overflowWrap: "anywhere" }}>{r.netsuite_type}</Typography>
                        </Box>
                        <CommentButton count={counts[targetId("record", r.id)] ?? 0} label={r.name} onClick={() => onComment(r.id)} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 18px" }}>
                        {by.map((b) => (
                          <Box key={b.source} sx={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", columnGap: 1, rowGap: "2px", alignItems: "baseline", maxWidth: { sm: 300 }, minWidth: 0 }}>
                            <Box sx={{ alignSelf: "center" }}><RoleMark role={b.role} /></Box>
                            <Typography variant="body2" fontWeight={600}>{label[b.source] ?? b.source}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: 2, lineHeight: 1.35 }}>{b.note}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }),
            ])}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="body2" color="text.secondary">{map.records_note}</Typography>
    </Stack>
  );
}
